import React, { useCallback, useEffect, useState, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { visuallyHidden, Typography, Box } from 'bonfire-kindling';
import { useTranslation } from 'react-i18next';
import { SORT_BY, PROJECT_STATUS } from 'constants/opportunities';
import useSearchOpportunities from './hooks/useSearchOpportunities';
import { Loader } from 'components';
import {
  PreviewOpportunitiesEmpty,
  BasicOpportunitiesCard,
  BasicOpportunitiesFilter,
  SignUpModal,
} from './components';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import { useQueryParams } from 'helpers/hooks';

function PreviewOpportunities() {
  const { t } = useTranslation();

  const query = useQueryParams();
  const [search, setSearch] = useState(query.get('search') || '');
  const [sortBy, setSortBy] = useState(SORT_BY.mostRecent);
  const [statuses, setStatuses] = useState(PROJECT_STATUS.allOpportunities);
  const [signUpModalOpen, setSignUpModalOpen] = useState(false);

  const {
    triggerSearch,
    fetchMore,
    data: projects,
    total,
    isLoading,
    shouldAppend,
  } = useSearchOpportunities({
    search,
    sortBy,
    statuses,
  });

  const triggerSearchDebounce = useMemo(() => debounce(triggerSearch, 500), [triggerSearch]);

  useEffect(() => {
    triggerSearchDebounce();
  }, [statuses, sortBy, triggerSearchDebounce]);

  useEffect(() => () => triggerSearchDebounce.cancel(), [triggerSearchDebounce]);
  useInfiniteScroll(fetchMore);

  const onFilterChange = useCallback(({ statuses }) => {
    if (statuses) {
      setStatuses(statuses);
      // Since we remove the "Closing Soon" option when filtering by closed
      // projects, the sort column also needs to be updated.
      if (statuses === PROJECT_STATUS.closed) {
        setSortBy(SORT_BY.closingSoon);
      }
    }
  }, []);

  const onSortChange = useCallback(e => {
    setSortBy(e.target.value);
  }, []);

  const onSearchChange = useCallback(
    e => {
      setSearch(e.target.value);

      triggerSearchDebounce();
    },
    [triggerSearchDebounce]
  );

  const onCloseSignUpModal = () => setSignUpModalOpen(false);

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{ minHeight: '100%', bgcolor: 'background.main', width: '100%' }}
    >
      <Typography component="h1" style={visuallyHidden}>
        {t('preview.screen_reader.opportunities')}
      </Typography>
      <SignUpModal open={signUpModalOpen} onClose={onCloseSignUpModal} />
      <Box m={2}>
        <BasicOpportunitiesFilter
          sortBy={sortBy}
          statuses={statuses}
          searchKeyword={search}
          onSortChange={onSortChange}
          onSearchChange={onSearchChange}
          onFilterChange={onFilterChange}
          totalCount={total}
        />
      </Box>
      {total === null || (isLoading && !shouldAppend) ? (
        <Loader variant="puff" isCentered />
      ) : (
        <>
          {projects.length === 0 && <PreviewOpportunitiesEmpty />}
          {projects.map(project => (
            <BasicOpportunitiesCard
              {...project}
              key={project.ProjectUUID}
              onClick={() => setSignUpModalOpen(true)}
            />
          ))}
          {projects.length < total && <Loader variant="puff" isCentered />}
        </>
      )}
    </Box>
  );
}

export default PreviewOpportunities;
